import React, { useState, useMemo, useEffect, useRef } from "react";
import { v4 as uuid_v4 } from "../../../libs/uuid";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  LinearProgress,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  Description as DescriptionIcon,
  Photo as PhotoIcon,
  PictureAsPdf as PdfIcon,
  TableChart as SpreadsheetIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  NoteAdd as NoteAddIcon,
} from "@mui/icons-material";
import { myToast } from "../../../libs";
import config from "../../../config";
import PreviewFile from "../../../components/files/PreviewFile";
import FileVisibilityToggle from "../../../components/files/FileVisibilityToggle";
import { apiGetSettings } from "../../../graphQL/settings";
import { apiGetAllAgents } from "../../../graphQL/agent";
import {
  addressBookPanelFooterSx,
  addressBookPanelHeaderSx,
} from "../../addressbook/panels/panelChrome";
import {
  apiCreateFile,
  apiGetFiles,
  apiUpdateFile,
  apiDeleteFile,
  apiGetFileSignedUrl,
} from "../../../graphQL/file";

// FileTypes will be loaded from Settings API

const FilesPanel = ({
  onClose,
  onCollapse,
  propertyInfo,
  updatePropertyInfo,
  uploadSingle,
  actionSave,
  filesDisplayName,
  setFilesDisplayName,
  filesSelectedType,
  setFilesSelectedType,
  filesSelectedFile,
  setFilesSelectedFile,
  filesStatus,
  setFilesStatus,
  filesProgress,
  setFilesProgress,
  triggerAddFile = 0,
  onOpenSearchTab,
  fileSettingsType = "property",
  fileRefCollection = "propertyAssets",
  fileRefSchemaType = "propertyAsset",
  emptySelectionMessage = "Select property first",
  previewEntityType = "property",
  entityName = "",
  fileFilters = {
    name: "",
    class: "",
    type: "",
    mimeType: "",
    date: "",
    dateTo: "",
  },
}) => {
  const [filterName] = useState("");
  const [filterClass] = useState("");
  const [filterType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isRegularDragOver, setIsRegularDragOver] = useState(false);
  const [isSystemDragOver, setIsSystemDragOver] = useState(false);
  const [isSystemSectionOpen, setIsSystemSectionOpen] = useState(true);
  const [isPublicSectionOpen, setIsPublicSectionOpen] = useState(true);
  const [isPropertySectionOpen, setIsPropertySectionOpen] = useState(true);
  const [isPublicDragOver, setIsPublicDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingIsSystem, setPendingIsSystem] = useState(false);
  const [pendingIsPublic, setPendingIsPublic] = useState(false);
  const nextPickIsSystemRef = useRef(false);
  const nextPickIsPublicRef = useRef(false);
  const draggedFileRef = useRef(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewInitialTab, setPreviewInitialTab] = useState(0);
  const [previewAutoGenerateSummary, setPreviewAutoGenerateSummary] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [loadingFileContent, setLoadingFileContent] = useState(false);
  const [showMetadataDialog, setShowMetadataDialog] = useState(false);
  const [filesTags, setFilesTags] = useState("");
  // Server-backed free-text search across the domain's files.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Metadata-only entry (a record with fields/notes but no uploaded file).
  const [showMetaOnlyDialog, setShowMetaOnlyDialog] = useState(false);
  const [metaOnlyName, setMetaOnlyName] = useState("");
  const [metaOnlyCategory, setMetaOnlyCategory] = useState("");
  const [metaOnlyType, setMetaOnlyType] = useState("Other");
  const [metaOnlyNotes, setMetaOnlyNotes] = useState("");
  const [metaOnlyTags, setMetaOnlyTags] = useState("");
  const [showPublicConfirmDialog, setShowPublicConfirmDialog] = useState(false);
  const [publicConfirmText, setPublicConfirmText] = useState("");
  const [publicConfirmChecked, setPublicConfirmChecked] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const privateInfo = useSelector((store) => store.auth.PrivateInfo);
  const authUser = useSelector((store) => store.auth.user);
  // Domain scope: the selected entity's domain, else the signed-in user's
  // ("All" is the super-admin sentinel, not a real domain).
  const effectiveDomain = useMemo(() => {
    const entityDomain = String(propertyInfo?.domain || "").trim();
    if (entityDomain) return entityDomain;
    const userDomain = String(authUser?.domain || "").trim();
    return userDomain && userDomain !== "All" ? userDomain : "";
  }, [propertyInfo?.domain, authUser?.domain]);
  const effectiveAuthorId = useMemo(() => {
    const signedInAuthorId = privateInfo?.id || privateInfo?.userId || "";
    return (
      signedInAuthorId ||
      propertyInfo?.authorId ||
      ""
    );
  }, [propertyInfo?.authorId, privateInfo?.id, privateInfo?.userId]);
  
  // Load fileTypes from Settings API (3-tier fallback: authorId → domain → defaults)
  const [fileTypesConfig, setFileTypesConfig] = useState([]);
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Open file picker when triggered externally (e.g. "+ File" nav button)
  useEffect(() => {
    if (!triggerAddFile) return;
    setSelectedCategory("");
    setFilesSelectedType("Other");
    setFilesDisplayName("");
    setTimeout(() => document.getElementById("files-panel-input")?.click(), 50);
  }, [triggerAddFile]);

  // Listen for camera file from Appbar camera button
  useEffect(() => {
    const handler = (e) => {
      const file = e.detail?.file;
      if (!file) return;
      setFilesSelectedFile(file);
      setPendingFile(file);
      setSelectedCategory("");
      setFilesSelectedType("Other");
      setFilesDisplayName(file.name.replace(/\.[^/.]+$/, ""));
      setShowMetadataDialog(true);
    };
    window.addEventListener("appCameraFile", handler);
    return () => window.removeEventListener("appCameraFile", handler);
  }, []);

  // Listen for public file upload requests from the overview brochure drop zone
  useEffect(() => {
    const handler = (e) => {
      // Public files attach to a contact/property — ignore the request if none is selected.
      if (!propertyInfo?.id) return;
      const file = e.detail?.file;
      if (file) {
        // Local file was dropped — open metadata dialog as public
        setFilesSelectedFile(file);
        setPendingFile(file);
        setSelectedCategory("");
        setFilesSelectedType("Other");
        setFilesDisplayName(file.name.replace(/\.[^/.]+$/, ""));
        setPendingIsSystem(false);
        setPendingIsPublic(true);
        setShowMetadataDialog(true);
      } else {
        // Click — open file picker as public
        nextPickIsPublicRef.current = true;
        setTimeout(() => document.getElementById("files-panel-input")?.click(), 50);
      }
    };
    window.addEventListener("requestPublicFileUpload", handler);
    return () => window.removeEventListener("requestPublicFileUpload", handler);
  }, [propertyInfo?.id]);

  useEffect(() => {
    const loadAgents = async () => {
      setLoadingAgents(true);
      try {
        const result = await apiGetAllAgents({});
        setAgents(result?.agents || []);
      } catch (error) {
        console.error("Failed to load agents:", error);
        setAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, []);

  useEffect(() => {
    const loadFileTypes = async () => {
      setLoadingFileTypes(true);
      try {
        const settingsInput = {
          schemaType: "fileTypes",
          type: fileSettingsType,
          authorId: effectiveAuthorId,
          domain: propertyInfo?.domain,
        };

        const result = await apiGetSettings(settingsInput);

        if (!result?.settings || result.settings.length === 0) {
          setFileTypesConfig([]);
          return;
        }

        // Backend handles 3-tier fallback automatically
        if (result?.settings?.[0]?.data) {
          // Convert flat list to hierarchical structure
          const fileTypesData = result.settings[0].data;

          if (!Array.isArray(fileTypesData) || fileTypesData.length === 0) {
            myToast.error(
              `FileTypes settings data is empty for query: ${JSON.stringify(
                settingsInput
              )}`
            );
            setFileTypesConfig([]);
            return;
          }
          // Group by category (extract from label: "Category, Type")
          // Only include enabled types from Settings. Disabled / unknown types fall into "Other".
          const grouped = {};
          fileTypesData
            .filter((item) => item && item.isActive !== false)
            .forEach((item) => {
              const label = String(item.label || "");
              const parts = label.split(",");
              const category = parts[0]?.trim() || "Other";
              const type = parts[1]?.trim() || label;

              if (!grouped[category]) {
                grouped[category] = [];
              }
              if (type && !grouped[category].includes(type)) {
                grouped[category].push(type);
              }
            });

          // Convert to array format
          const configArray = Object.keys(grouped).map((category) => ({
            class: category,
            types: grouped[category],
          }));

          setFileTypesConfig(configArray);
        } else {
          myToast.error(
            `FileTypes settings document has no data for query: ${JSON.stringify(
              settingsInput
            )}`
          );
          setFileTypesConfig([]);
        }
      } catch (error) {
        console.error("[DEBUG] Error loading fileTypes:", error);
        setFileTypesConfig([]);
      } finally {
        setLoadingFileTypes(false);
      }
    };

    if (propertyInfo?.authorId || propertyInfo?.domain || propertyInfo?.id) {
      loadFileTypes();
    }
  }, [effectiveAuthorId, fileSettingsType, propertyInfo?.domain, propertyInfo?.id]);

  // console.log("property info :: ",propertyInfo)

  // Query File collection by reference
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  const refreshFiles = async () => {
    setLoadingFiles(true);
    try {
      // Active search: one server-side domain-wide query instead of the
      // entity/system pair. Matches description, summary, category/type,
      // tags and notes on the backend.
      const q = String(searchQuery || "").trim();
      if (q && effectiveDomain) {
        const result = await apiGetFiles({
          domain: effectiveDomain,
          q,
          pagination: { page: 1, limit: 200 },
        });
        setFiles(result.files || []);
        return;
      }

      // Fetch property/contact files (only when an entity is selected) and system files in parallel.
      const queries = [];
      if (propertyInfo?.id) {
        queries.push(
          apiGetFiles({
            refCollection: fileRefCollection,
            refId: propertyInfo.id,
            authorId: effectiveAuthorId,
            domain: effectiveDomain || undefined,
          })
        );
      } else {
        queries.push(Promise.resolve({ files: [] }));
      }

      // Fetch system files by domain using isSystem flag — independent of any selection.
      queries.push(
        apiGetFiles({
          domain: effectiveDomain || undefined,
          isSystem: true,
        }).catch(() => ({ files: [] }))
      );
      const [propertyResult, systemResult] = await Promise.all(queries);

      const propertyFiles = propertyResult.files || [];
      const systemFiles = systemResult.files || [];
      // Merge with property files, avoiding duplicates
      const propertyFileIds = new Set(propertyFiles.map((f) => f.id));
      const merged = [
        ...propertyFiles,
        ...systemFiles.filter((f) => !propertyFileIds.has(f.id)),
      ];
      setFiles(merged);
    } catch (error) {
      console.error("Error fetching files:", error);
      myToast.error("Failed to refresh files");
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    refreshFiles();
  }, [
    effectiveAuthorId,
    fileRefCollection,
    propertyInfo?.id,
    effectiveDomain,
  ]);

  // Debounced server search while typing; clearing restores the normal lists.
  useEffect(() => {
    const timer = setTimeout(() => {
      refreshFiles();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Helper: Check if file is a system/domain file (read-only)
  const isSystemFile = (file) => file?.isSystem === true || file?.isDomainFile === true;

  // Whether a contact/property is currently selected. When false, only the
  // System Files zone accepts uploads — the Public and Property/Contact zones
  // are disabled at the source so users can't initiate an upload that has
  // nowhere to attach.
  const hasEntity = !!propertyInfo?.id;

  // Handler: Copy system file to property (uses existing category/type from domain)
  const handleCopySystemFile = async (systemFile) => {
    try {
      if (!propertyInfo?.id) {
        myToast.error(emptySelectionMessage);
        return;
      }

      // Use category and type already set in domain file
      const fileCategory = systemFile.category || "Templates";
      const fileType = systemFile.type || "Document";

      // 1. Create File collection document
      const fileDoc = await apiCreateFile({
        authorId: effectiveAuthorId,
        fileCategory: fileCategory,
        type: fileType,
        mimeType: systemFile.mimeType || "application/octet-stream",
        url: systemFile.url,
        key: systemFile.key,
        size: String(systemFile.size || 0),
        description: systemFile.description || "",
        domain: effectiveDomain || systemFile.domain || "myrighthand",
        ref: {
          id: propertyInfo.id,
          collection: fileRefCollection,
        },
        status: "active",
        isPublic: false,
      });

      // File is linked by file.ref, no property update needed.
      // Refresh files list (includes both property and system files)
      await refreshFiles();

      myToast.success(`File "${systemFile.description || systemFile.name || "File"}" copied to ${fileSettingsType === "contact" ? "Contact" : "Property"} Files`);
    } catch (error) {
      console.error("Failed to copy system file:", error);
      myToast.error("Failed to copy file");
    }
  };

  // Build category types and sub-types from JSON config
  const categoryTypes = useMemo(() => {
    if (!Array.isArray(fileTypesConfig)) return [];
    const classes = fileTypesConfig.map((item) => item?.class).filter(Boolean);
    return classes;
  }, [fileTypesConfig]);

  const subTypesByCategory = useMemo(() => {
    const map = {};
    if (Array.isArray(fileTypesConfig)) {
      fileTypesConfig.forEach((item) => {
        if (item?.class && Array.isArray(item.types)) {
          map[item.class] = item.types;
        }
      });
    }
    return map;
  }, [fileTypesConfig]);

  const currentSubTypes = useMemo(() => {
    if (!selectedCategory) return [];
    return Array.isArray(subTypesByCategory[selectedCategory])
      ? subTypesByCategory[selectedCategory]
      : [];
  }, [selectedCategory, subTypesByCategory]);

  // Pre-compute MenuItem children for Type Select to ensure they're always valid
  // If a class is selected, show only types for that class; otherwise show all types
  const typeSelectMenuItems = useMemo(() => {
    // If a class is selected, show only types for that class
    if (selectedCategory) {
      if (!Array.isArray(currentSubTypes) || currentSubTypes.length === 0) {
        return [];
      }
      return currentSubTypes
        .filter(
          (type) =>
            type != null &&
            typeof type === "string" &&
            String(type).trim() !== "" &&
            String(type).trim().toLowerCase() !== "other"
        )
        .map((type) => {
          const typeStr = String(type || "").trim();
          if (!typeStr) return null;
          return (
            <MenuItem key={typeStr} value={typeStr} sx={{ fontSize: "13px" }}>
              {typeStr}
            </MenuItem>
          );
        })
        .filter((item) => item != null);
    }

    // If no class is selected, show all types from all classes
    if (!Array.isArray(fileTypesConfig) || fileTypesConfig.length === 0) {
      return [];
    }
    return fileTypesConfig
      .filter(
        (item) =>
          item &&
          item.class &&
          Array.isArray(item.types) &&
          item.types.length > 0
      )
      .flatMap((item) => {
        return item.types
          .filter(
            (type) =>
              type != null &&
              typeof type === "string" &&
              String(type).trim() !== "" &&
              String(type).trim().toLowerCase() !== "other"
          )
          .map((type) => {
            const typeStr = String(type || "").trim();
            if (!typeStr) return null;
            return (
              <MenuItem key={typeStr} value={typeStr} sx={{ fontSize: "13px" }}>
                {typeStr}
              </MenuItem>
            );
          })
          .filter((item) => item != null);
      });
  }, [selectedCategory, currentSubTypes, fileTypesConfig]);

  // Pre-compute MenuItem children for Class Select (Add File - no System Files)
  const classSelectMenuItems = useMemo(() => {
    if (!Array.isArray(categoryTypes) || categoryTypes.length === 0) {
      return [];
    }
    return categoryTypes
      .filter((type) => type != null && String(type).trim() !== "")
      .map((type) => {
        const typeStr = String(type || "").trim();
        if (!typeStr) return null;
        return (
          <MenuItem key={typeStr} value={typeStr} sx={{ fontSize: "13px" }}>
            {typeStr}
          </MenuItem>
        );
      })
      .filter((item) => item != null);
  }, [categoryTypes]);

  // Helper functions
  const getFileExtension = (file) => {
    if (file?.originalName) {
      const ext = file.originalName.split(".").pop()?.toLowerCase();
      if (ext && ext.length <= 5) return ext;
    }
    if (file?.name) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext && ext.length <= 5 && ext !== file.name.toLowerCase()) return ext;
    }
    if (file?.url) {
      try {
        const pathname = new URL(file.url).pathname;
        const ext = pathname.split(".").pop()?.toLowerCase();
        if (ext && ext.length <= 5) return ext;
      } catch {
        const cleanUrl = file.url.split("?")[0];
        const ext = cleanUrl.split(".").pop()?.toLowerCase();
        if (ext && ext.length <= 5) return ext;
      }
    }
    return null;
  };

  const getFileIcon = (file) => {
    const ext = getFileExtension(file);
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext)) {
      return <PhotoIcon sx={{ fontSize: "14px" }} />;
    }
    if (ext === "pdf") {
      return <PdfIcon sx={{ fontSize: "14px" }} />;
    }
    if (["xlsx", "xls", "docx", "doc", "pptx", "ppt"].includes(ext)) {
      return <SpreadsheetIcon sx={{ fontSize: "14px" }} />;
    }
    if (["mp4", "webm", "mov", "avi"].includes(ext)) {
      return <VideoIcon sx={{ fontSize: "14px" }} />;
    }
    if (["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) {
      return <AudioIcon sx={{ fontSize: "14px" }} />;
    }
    return <FileIcon sx={{ fontSize: "14px" }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getUploadFileExtension = (file) => {
    const fromName = String(file?.name || "")
      .split(".")
      .pop()
      ?.trim()
      .toLowerCase();
    if (fromName && fromName !== String(file?.name || "").toLowerCase()) {
      return fromName;
    }
    const fromMime = String(file?.type || "")
      .split("/")
      .pop()
      ?.trim()
      .toLowerCase();
    return fromMime || "";
  };

  // CRE context layer: docType/version caption suffix and superseded-version
  // collapsing. One helper each, shared by all three list renders.
  const getFileCaptionExtras = (file) => {
    const parts = [];
    if (file?.docType && file.docType !== "other") parts.push(String(file.docType).toUpperCase());
    if (Number(file?.version) > 1) parts.push(`v${file.version}`);
    return parts.length ? ` • ${parts.join(" ")}` : "";
  };
  const withoutSuperseded = (list) => (list || []).filter((f) => f?.isCurrent !== false);

  // Helper to get display name for file
  const getFileDisplayName = (file) => {
    // Priority: description (user-provided) > originalName > name > extract from URL/key
    if (file?.description && file.description.trim()) {
      return file.description;
    }
    if (file?.originalName) {
      return file.originalName;
    }
    if (file?.name) {
      return file.name;
    }
    // Extract filename from URL or key
    if (file?.key) {
      const parts = file.key.split("/");
      const filename = parts[parts.length - 1];
      // Remove timestamp prefix if present (e.g., "1234567890_filename.pdf" -> "filename.pdf")
      const cleanName = filename.replace(/^\d+_/, "");
      return cleanName || filename;
    }
    if (file?.url) {
      try {
        const pathname = new URL(file.url).pathname;
        const parts = pathname.split("/");
        const filename = parts[parts.length - 1];
        const cleanName = filename.replace(/^\d+_/, "");
        return cleanName || filename;
      } catch {
        const parts = file.url.split("/");
        const filename = parts[parts.length - 1].split("?")[0];
        const cleanName = filename.replace(/^\d+_/, "");
        return cleanName || filename;
      }
    }
    return "Untitled File";
  };

  // File type detection helpers
  const isImageFile = (file) => {
    if (String(file?.mimeType || "").toLowerCase().startsWith("image/")) return true;
    const ext = getFileExtension(file);
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
  };

  const isPDFFile = (file) => {
    if (String(file?.mimeType || "").toLowerCase().includes("pdf")) return true;
    const ext = getFileExtension(file);
    return ext === "pdf";
  };

  const isTextFile = (file) => {
    const mime = String(file?.mimeType || "").toLowerCase();
    if (
      mime.startsWith("text/") ||
      [
        "application/json",
        "application/xml",
        "application/javascript",
        "application/x-javascript",
        "application/yaml",
        "application/x-yaml",
      ].includes(mime)
    ) {
      return true;
    }
    const ext = getFileExtension(file);
    return [
      "txt",
      "json",
      "csv",
      "xml",
      "md",
      "log",
      "yaml",
      "yml",
      "html",
      "css",
      "js",
      "jsx",
      "ts",
      "tsx",
      "py",
      "java",
      "cpp",
      "c",
      "h",
    ].includes(ext);
  };

  const isVideoFile = (file) => {
    const ext = getFileExtension(file);
    if (["mp4", "webm", "ogg", "mov", "avi", "m4v", "3gp", "mkv"].includes(ext)) return true;
    if (file?.mimeType?.startsWith("video/")) return true;
    return false;
  };

  const isAudioFile = (file) => {
    if (String(file?.mimeType || "").toLowerCase().startsWith("audio/")) return true;
    const ext = getFileExtension(file);
    return ["mp3", "wav", "ogg", "m4a", "aac"].includes(ext);
  };

  const isOfficeFile = (file) => {
    const mime = String(file?.mimeType || "").toLowerCase();
    if (
      mime.includes("officedocument") ||
      mime.includes("msword") ||
      mime.includes("excel") ||
      mime.includes("powerpoint")
    ) {
      return true;
    }
    const ext = getFileExtension(file);
    return ["xlsx", "xls", "docx", "doc", "pptx", "ppt"].includes(ext);
  };

  // Fetch text file content for preview
  const fetchFileContent = async (url) => {
    setLoadingFileContent(true);
    try {
      const response = await fetch(url);
      const text = await response.text();
      setFileContent(text);
    } catch (error) {
      console.error("Failed to fetch file content:", error);
      setFileContent("Error loading file content.");
    } finally {
      setLoadingFileContent(false);
    }
  };

  useEffect(() => {
    if (previewFile && isTextFile(previewFile)) {
      fetchFileContent(previewFile.url);
    } else {
      setFileContent("");
    }
  }, [previewFile]);

  const getFileClass = (file) => {
    const configured = Array.isArray(fileTypesConfig) ? fileTypesConfig : [];

    const fileCategoryRaw =
      file?.fileCategory != null ? String(file.fileCategory) : "";
    const fileTypeRaw = file?.type != null ? String(file.type) : "";

    // If a file has a stored category (fileCategory), only keep it if its type is enabled under that category
    if (fileCategoryRaw && fileTypeRaw) {
      const classConfig = configured.find(
        (c) => c && c.class === fileCategoryRaw
      );
      if (
        classConfig &&
        Array.isArray(classConfig.types) &&
        classConfig.types.includes(fileTypeRaw)
      ) {
        return fileCategoryRaw;
      }
      return "Other";
    }

    // If missing category, we do not trust type alone (it may be generic like "Document").
    // Per requirement: anything not enabled / not categorized should go to Other.
    return "Other";
  };

  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
  };

  const parseTags = (raw) =>
    String(raw || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  const performDirectUpload = (file, category, type, description) => {
    if (!pendingIsSystem && !propertyInfo?.id) {
      myToast.error(emptySelectionMessage);
      return;
    }
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    setFilesStatus("Uploading...");
    setFilesProgress(0);

    const fileId = uuid_v4();
    const domainKey = String(effectiveDomain || "myrighthand").trim() || "myrighthand";
    const fileName = fileId;
    const directory = pendingIsSystem
      ? `${domainKey}/system/files`
      : `${domainKey}/${propertyInfo?.id}/files`;
    const extension = getUploadFileExtension(file);
    const publicObjectName = extension ? `${fileId}.${extension}` : fileId;
    const uploadKey = pendingIsPublic
      ? `${domainKey}/${publicObjectName}`
      : `${directory}/${fileName}`;
    const displayName = (description || "").trim() || file.name.replace(/\.[^/.]+$/, "");
    const publicUrl = `${config.URL.REACT_APP_PUBLIC_ASSETS_BASE_URL}/${domainKey}/${publicObjectName}`;
    const privateUrl = `${config.ENV.REACT_APP_HLS_BASE_URL}/${directory}/${fileName}`;

    uploadSingle(
      directory,
      file,
      fileName,
      (uploadProgress) => { setFilesProgress(uploadProgress); },
      async (err, result, callbackDone) => {
        if (err) {
          setFilesStatus("");
          setFilesProgress(0);
          const uploadMessage =
            err?.message ||
            err?.graphQLErrors?.[0]?.message ||
            "Upload failed";
          myToast.error(uploadMessage);
          return;
        }
        try {
          const effectiveFileCategory = category || "";
          await apiCreateFile({
            authorId: effectiveAuthorId,
            fileCategory: effectiveFileCategory,
            type: type || "Other",
            fileType: extension,
            mimeType: file.type || "application/octet-stream",
            url: pendingIsPublic ? publicUrl : privateUrl,
            publicUrl,
            key: uploadKey,
            size: String(file.size),
            description: displayName,
            domain: domainKey,
            isSystem: pendingIsSystem,
            ...(parseTags(filesTags).length
              ? { data: { tags: parseTags(filesTags) } }
              : {}),
            ref: pendingIsSystem
              ? { id: "", collection: "" }
              : { id: propertyInfo.id, collection: fileRefCollection },
            status: "active",
            isPublic: pendingIsPublic,
          });
          await refreshFiles();
          setFilesStatus("");
          setFilesProgress(0);
          setFilesTags("");
          myToast.success("File uploaded successfully");
        } catch (saveErr) {
          console.error("Failed to save file:", saveErr);
          setFilesStatus("");
          const message =
            saveErr?.message ||
            saveErr?.graphQLErrors?.[0]?.message ||
            "Upload failed to save";
          myToast.error(message);
        }
        callbackDone && callbackDone();
      },
      { key: uploadKey, isPublic: pendingIsPublic }
    );
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isSystem = nextPickIsSystemRef.current;
      const isPublic = nextPickIsPublicRef.current;
      nextPickIsSystemRef.current = false;
      nextPickIsPublicRef.current = false;
      setFilesSelectedFile(file);
      setPendingFile(file);
      setSelectedCategory("");
      setFilesSelectedType("Other");
      setFilesDisplayName(file.name.replace(/\.[^/.]+$/, ""));
      setPendingIsSystem(isSystem);
      setPendingIsPublic(isPublic);
      setShowMetadataDialog(true);
    }
  };

  const performUpload = (fileToUpload) => {
    if (!pendingIsSystem && !propertyInfo?.id) {
      myToast.error(emptySelectionMessage);
      return;
    }
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }

    setFilesStatus("Uploading...");
    setFilesProgress(0);

    const fileId = uuid_v4();
    const domainKey = String(effectiveDomain || "myrighthand").trim() || "myrighthand";
    const fileName = fileId;
    const directory = pendingIsSystem
      ? `${domainKey}/system/files`
      : `${domainKey}/${propertyInfo?.id}/files`;
    const extension = getUploadFileExtension(fileToUpload);
    const publicObjectName = extension ? `${fileId}.${extension}` : fileId;
    const uploadKey = pendingIsPublic
      ? `${domainKey}/${publicObjectName}`
      : `${directory}/${fileName}`;
    const publicUrl = `${config.URL.REACT_APP_PUBLIC_ASSETS_BASE_URL}/${domainKey}/${publicObjectName}`;
    const privateUrl = `${config.ENV.REACT_APP_HLS_BASE_URL}/${directory}/${fileName}`;

    const displayName = String(filesDisplayName || "").trim();
    const effectiveFileType = String(filesSelectedType || "Other").trim() || "Other";
    const effectiveFileCategory = String(selectedCategory || "Other").trim() || "Other";

    uploadSingle(
      directory,
      fileToUpload,
      fileName,
      (uploadProgress) => {
        setFilesProgress(uploadProgress);
      },
      async (err, result, callbackDone) => {
        if (err) {
          setFilesStatus("");
          setFilesProgress(0);
          const uploadMessage =
            err?.message ||
            err?.graphQLErrors?.[0]?.message ||
            "Upload failed";
          myToast.error(uploadMessage);
          return;
        }

        try {
          // Create File document in File collection
          const fileDoc = await apiCreateFile({
            authorId: effectiveAuthorId,
            fileCategory: effectiveFileCategory,
            type: effectiveFileType,
            fileType: extension,
            mimeType: fileToUpload.type || "application/octet-stream",
            url: pendingIsPublic ? publicUrl : privateUrl,
            publicUrl,
            key: uploadKey,
            size: String(fileToUpload.size),
            description: displayName,
            domain: domainKey,
            isSystem: pendingIsSystem,
            ...(parseTags(filesTags).length
              ? { data: { tags: parseTags(filesTags) } }
              : {}),
            ref: pendingIsSystem
              ? { id: "", collection: "" }
              : { id: propertyInfo.id, collection: fileRefCollection },
            status: "active",
            isPublic: pendingIsPublic,
          });

          // No property update needed - files are linked by file.ref
          await refreshFiles();

          setFilesStatus("");
          setFilesProgress(0);
          myToast.success("File uploaded successfully");
          setFilesDisplayName("");
          setFilesSelectedFile(null);
          setPendingFile(null);
          setSelectedCategory("");
          setFilesSelectedType("Other");
          setFilesTags("");
          setPendingIsSystem(false);
          setPendingIsPublic(false);
          const fileInput = document.getElementById("files-panel-input");
          if (fileInput) fileInput.value = "";
        } catch (saveErr) {
          console.error("Failed to save file:", saveErr);
          setFilesStatus("");
          const message =
            saveErr?.message ||
            saveErr?.graphQLErrors?.[0]?.message ||
            "Upload failed to save";
          myToast.error(message);
        }

        callbackDone && callbackDone();
      },
      { key: uploadKey, isPublic: pendingIsPublic }
    );
  };

  const handleCloseMetadataDialog = () => {
    setShowMetadataDialog(false);
    setPendingFile(null);
    setFilesSelectedFile(null);
    setSelectedCategory("");
    setFilesSelectedType("Other");
    setFilesDisplayName("");
    setFilesTags("");
    setPendingIsSystem(false);
    setPendingIsPublic(false);
    const fileInput = document.getElementById("files-panel-input");
    if (fileInput) fileInput.value = "";
  };

  const handleConfirmMetadataDialog = () => {
    if (!pendingFile) {
      handleCloseMetadataDialog();
      return;
    }
    if (pendingIsPublic) {
      // Show public confirmation dialog instead of uploading immediately
      setShowMetadataDialog(false);
      setPublicConfirmText("");
      setPublicConfirmChecked(false);
      setShowPublicConfirmDialog(true);
      return;
    }
    const fileToUpload = pendingFile;
    setShowMetadataDialog(false);
    setPendingFile(null);
    performUpload(fileToUpload);
  };

  const handleConfirmPublicUpload = () => {
    setShowPublicConfirmDialog(false);
    setPublicConfirmText("");
    setPublicConfirmChecked(false);

    // Handle move-to-public (drag between sections)
    if (pendingMoveToPublicRef.current) {
      const file = pendingMoveToPublicRef.current;
      pendingMoveToPublicRef.current = null;
      executeMoveFileToPublic(file);
      return;
    }

    // Handle new file upload
    if (!pendingFile) return;
    const fileToUpload = pendingFile;
    setPendingFile(null);
    performUpload(fileToUpload);
  };

  const handleCancelPublicUpload = () => {
    setShowPublicConfirmDialog(false);
    setPublicConfirmText("");
    setPublicConfirmChecked(false);
    pendingMoveToPublicRef.current = null;
    setPendingFile(null);
    setPendingIsPublic(false);
    const fileInput = document.getElementById("files-panel-input");
    if (fileInput) fileInput.value = "";
  };

  const handleDeleteFile = async (file) => {
    const fileId = file?.id;
    const deleteAuthorId = file?.authorId || effectiveAuthorId;
    if (!fileId || !deleteAuthorId) {
      myToast.error("Delete failed");
      return;
    }
    try {
      // Delete the File document from File collection
      await apiDeleteFile(fileId, deleteAuthorId);

      // Optimistically remove, then refresh from source of truth.
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      await refreshFiles();

      myToast.success("File deleted");
    } catch (err) {
      console.error("Failed to delete file:", err);
      myToast.error("Delete failed");
    }
  };

  const handleDownloadFile = async (file) => {
    // Metadata-only entries have nothing in S3.
    if (!file?.url && !file?.key) {
      myToast.info("Metadata-only entry — no file to download");
      return;
    }
    if (file?.isPublic && file?.publicUrl) {
      window.open(file.publicUrl, "_blank");
      return;
    }
    // Private files: use a signed URL so downloads don't depend on the CDN
    // route. Falls back to the stored url (e.g. files owned by another user).
    try {
      const signedUrl = await apiGetFileSignedUrl(file.id, effectiveAuthorId);
      window.open(signedUrl, "_blank");
    } catch (error) {
      if (file?.url) {
        window.open(file.url, "_blank");
      } else {
        myToast.error("Could not generate download link");
      }
    }
  };

  const handleCreateMetadataEntry = async () => {
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    const domainKey =
      String(effectiveDomain || "myrighthand").trim() || "myrighthand";
    const name = String(metaOnlyName || "").trim();
    if (!name) {
      myToast.error("Name is required");
      return;
    }
    try {
      const tags = parseTags(metaOnlyTags);
      const notes = String(metaOnlyNotes || "").trim();
      await apiCreateFile({
        authorId: effectiveAuthorId,
        description: name,
        fileCategory: metaOnlyCategory || "",
        type: metaOnlyType || "Other",
        schemaType: "metadata",
        mimeType: "",
        size: "",
        url: "",
        publicUrl: "",
        key: "",
        domain: domainKey,
        isSystem: !hasEntity,
        isPublic: false,
        data: {
          ...(notes ? { notes } : {}),
          ...(tags.length ? { tags } : {}),
        },
        ref: hasEntity
          ? { id: propertyInfo.id, collection: fileRefCollection }
          : { id: "", collection: "" },
        status: "active",
      });
      setShowMetaOnlyDialog(false);
      setMetaOnlyName("");
      setMetaOnlyCategory("");
      setMetaOnlyType("Other");
      setMetaOnlyNotes("");
      setMetaOnlyTags("");
      await refreshFiles();
      myToast.success("Metadata entry added");
    } catch (error) {
      console.error("Failed to create metadata entry:", error);
      myToast.error(
        error?.message ||
          error?.graphQLErrors?.[0]?.message ||
          "Failed to add metadata entry"
      );
    }
  };

  const openFilePreview = (file, options = {}) => {
    setPreviewInitialTab(options.initialTab ?? 0);
    setPreviewAutoGenerateSummary(Boolean(options.autoGenerateSummary));
    setPreviewFile(file);
  };

  const handleToggleFileVisibility = async (file) => {
    if (!file?.id || isSystemFile(file)) return;

    const nextIsPublic = !Boolean(file?.isPublic);
    try {
      await apiUpdateFile({
        id: file.id,
        authorId: effectiveAuthorId,
        isPublic: nextIsPublic,
      });

      setFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === file.id ? { ...item, isPublic: nextIsPublic } : item
        )
      );

      setPreviewFile((prevFile) =>
        prevFile?.id === file.id
          ? { ...prevFile, isPublic: nextIsPublic }
          : prevFile
      );

      myToast.success(`File is now ${nextIsPublic ? "Public" : "Private"}`);
    } catch (error) {
      console.error("Failed to update file visibility:", error);
      myToast.error("Failed to update file visibility");
    }
  };

  /**
   * Handle saving the summary back to the file
   * fileId is passed when saving in background for a different file
   */
  const handleSaveSummary = async ({
    summary,
    prompt,
    fileId,
    aiFileID,
    extractedMetadata,
    metadataPrompt,
    fileDocumentId,
  }) => {
    const targetFileId = fileId || previewFile?.id;
    if (!targetFileId) return;

    try {
      // Update File document directly in File collection
      const updateData = {
        id: targetFileId,
        authorId: effectiveAuthorId,
        summary: summary,
        data: {
          extractedMetadata: extractedMetadata,
          prompt: prompt,
          metadataPrompt: metadataPrompt,
        },
      };

      // Only include fields that are defined
      if (aiFileID !== undefined) {
        updateData.aiFileId = aiFileID;
      }

      console.log("[DEBUG] Updating File document:", updateData);
      const updatedFile = await apiUpdateFile(updateData);
      console.log("[DEBUG] File updated successfully:", updatedFile);

      // Update local file state
      setFiles(
        files.map((f) => {
          if (f.id === targetFileId) {
            return {
              ...f,
              summary: summary !== undefined ? summary : f.summary,
              data: {
                ...(f.data || {}),
                extractedMetadata:
                  extractedMetadata !== undefined
                    ? extractedMetadata
                    : f.data?.extractedMetadata,
                prompt: prompt !== undefined ? prompt : f.data?.prompt,
                metadataPrompt:
                  metadataPrompt !== undefined
                    ? metadataPrompt
                    : f.data?.metadataPrompt,
              },
            };
          }
          return f;
        })
      );

      // Update preview file state
      if (previewFile?.id === targetFileId) {
        setPreviewFile((prev) => ({
          ...prev,
          summary: summary !== undefined ? summary : prev.summary,
          data: {
            ...(prev.data || {}),
            extractedMetadata:
              extractedMetadata !== undefined
                ? extractedMetadata
                : prev.data?.extractedMetadata,
            prompt: prompt !== undefined ? prompt : prev.data?.prompt,
            metadataPrompt:
              metadataPrompt !== undefined
                ? metadataPrompt
                : prev.data?.metadataPrompt,
          },
        }));
      }

      console.log(`File data saved for file ${targetFileId}`);
    } catch (error) {
      console.error("Failed to save file data:", error);
      myToast.error("Failed to save file data");
    }
  };

  const filteredFiles = useMemo(() => {
    const externalFilterName = String(fileFilters?.name || "").trim();
    const externalFilterClass = String(fileFilters?.class || "").trim();
    const externalFilterType = String(fileFilters?.type || "").trim();
    const externalFilterMimeType = String(fileFilters?.mimeType || "").trim();
    const externalFilterDate = String(fileFilters?.date || "").trim();
    const externalFilterDateTo = String(fileFilters?.dateTo || "").trim();

    // If System Files is selected, don't show property files
    if (externalFilterClass === "System Files") {
      return [];
    }

    let filtered = files.filter((file) => {
      const matchesName =
        !externalFilterName ||
        file.name?.toLowerCase().includes(externalFilterName.toLowerCase()) ||
        file.originalName?.toLowerCase().includes(externalFilterName.toLowerCase());
      const fileClass = getFileClass(file);
      const matchesClass = !externalFilterClass || fileClass === externalFilterClass;
      const matchesType = !externalFilterType || file.type === externalFilterType;
      const matchesMime = !externalFilterMimeType || (file.mimeType || "").toLowerCase().includes(externalFilterMimeType.toLowerCase());
      const matchesDate = !externalFilterDate || (file.createdAt && new Date(file.createdAt) >= new Date(externalFilterDate));
      const matchesDateTo = !externalFilterDateTo || (file.createdAt && new Date(file.createdAt) <= new Date(externalFilterDateTo + "T23:59:59"));
      return matchesName && matchesClass && matchesType && matchesMime && matchesDate && matchesDateTo;
    });

    return filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [files, fileFilters, fileTypesConfig]);

  // Separate files with empty ref (system files) from entity-specific files
  const hasRef = (file) => {
    const ref = file?.ref;
    if (!ref) return false;
    if (typeof ref === "object" && !ref.id && !ref.collection) return false;
    return Boolean(ref.id);
  };

  const filteredDomainFiles = useMemo(() => {
    const externalFilterName = String(fileFilters?.name || "").trim();
    const externalFilterClass = String(fileFilters?.class || "").trim();
    const externalFilterType = String(fileFilters?.type || "").trim();
    const externalFilterDate = String(fileFilters?.date || "").trim();
    const externalFilterDateTo = String(fileFilters?.dateTo || "").trim();

    // Build system/domain candidates from both legacy prop and live queried files.
    const systemCandidates = (() => {
      const seen = new Set();
      const merged = [];
      const push = (file) => {
        if (!file?.id || seen.has(file.id)) return;
        seen.add(file.id);
        merged.push(file);
      };

      (files || [])
        .filter(
          (file) =>
            file?.isSystem === true ||
            !hasRef(file)
        )
        .forEach(push);

      return merged;
    })();

    // If System Files is selected, show only system/domain files (filter by name and type only)
    const sortByNewest = (arr) => [...arr].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    if (externalFilterClass === "System Files") {
      return sortByNewest(systemCandidates.filter((file) => {
        const matchesName =
          !externalFilterName ||
          file.name?.toLowerCase().includes(externalFilterName.toLowerCase()) ||
          file.originalName?.toLowerCase().includes(externalFilterName.toLowerCase());
        const matchesType = !externalFilterType || file.type === externalFilterType;
        const matchesDate = !externalFilterDate || (file.createdAt && new Date(file.createdAt) >= new Date(externalFilterDate));
        const matchesDateTo = !externalFilterDateTo || (file.createdAt && new Date(file.createdAt) <= new Date(externalFilterDateTo + "T23:59:59"));
        return matchesName && matchesType && matchesDate && matchesDateTo;
      }));
    }

    // If no filter is selected, show system/domain files (filter by name and type only)
    if (!externalFilterClass) {
      return sortByNewest(systemCandidates.filter((file) => {
        const matchesName =
          !externalFilterName ||
          file.name?.toLowerCase().includes(externalFilterName.toLowerCase()) ||
          file.originalName?.toLowerCase().includes(externalFilterName.toLowerCase());
        const matchesType = !externalFilterType || file.type === externalFilterType;
        const matchesDate = !externalFilterDate || (file.createdAt && new Date(file.createdAt) >= new Date(externalFilterDate));
        const matchesDateTo = !externalFilterDateTo || (file.createdAt && new Date(file.createdAt) <= new Date(externalFilterDateTo + "T23:59:59"));
        return matchesName && matchesType && matchesDate && matchesDateTo;
      }));
    }

    // Otherwise, don't show domain files (only show property files when a class is selected)
    return [];
  }, [files, fileFilters]);

  const entityFiles = useMemo(() => withoutSuperseded(filteredFiles.filter((f) => !f.isSystem && !f.isPublic && hasRef(f))), [filteredFiles]);
  const publicFiles = useMemo(() => withoutSuperseded(filteredFiles.filter((f) => !f.isSystem && f.isPublic && hasRef(f))), [filteredFiles]);
  useMemo(() => [], []); // preserve hook order

  // Group entity files by class
  const groupedFiles = useMemo(() => {
    const groups = {};
    entityFiles.forEach((file) => {
      const fileClass = getFileClass(file);
      if (!groups[fileClass]) {
        groups[fileClass] = [];
      }
      groups[fileClass].push(file);
    });
    return groups;
  }, [entityFiles]);

  const allFileTypes = useMemo(() => {
    return fileTypesConfig.flatMap((item) => item.types);
  }, [fileTypesConfig]);


  const prepareDroppedFile = (file, isSystem = false) => {
    setFilesSelectedFile(file);
    setPendingFile(file);
    setSelectedCategory("");
      setFilesSelectedType("Other");
      setFilesDisplayName(file.name.replace(/\.[^/.]+$/, ""));
      setPendingIsSystem(isSystem);
      setPendingIsPublic(false);
      setShowMetadataDialog(true);
  };

  const handleDragOver = (setter) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setter(true);
  };

  const handleDragLeave = (setter) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setter(false);
    }
  };

  const handleMoveFileToSystem = async (file) => {
    if (!file?.id) return;
    try {
      await apiUpdateFile({
        id: file.id,
        authorId: effectiveAuthorId,
        domain: propertyInfo?.domain || "",
        isSystem: true,
        ref: { id: "", collection: "" },
      });
      // Optimistic local update — clear ref so it appears in system files
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? { ...f, domain: propertyInfo?.domain || "", isSystem: true, ref: { id: "", collection: "" } }
            : f
        )
      );
      myToast.success(`"${file.description || file.name || "File"}" moved to System Files`);
    } catch (error) {
      console.error("Failed to move file to system:", error);
      myToast.error("Failed to move file");
    }
  };

  const handleMoveFileToProperty = async (file) => {
    if (!file?.id || !propertyInfo?.id) return;
    try {
      await apiUpdateFile({
        id: file.id,
        authorId: effectiveAuthorId,
        isSystem: false,
        isPublic: false,
        ref: { id: propertyInfo.id, collection: fileRefCollection },
      });
      // Optimistic local update — set ref so it appears in entityFiles
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? {
                ...f,
                isSystem: false,
                isPublic: false,
                ref: { id: propertyInfo.id, collection: fileRefCollection },
              }
            : f
        )
      );
      myToast.success(
        `"${file.description || file.name || "File"}" moved to ${
          fileSettingsType === "contact" ? "Contact" : "Property"
        } Files`
      );
    } catch (error) {
      console.error("Failed to move file to property:", error);
      myToast.error("Failed to move file");
    }
  };

  const pendingMoveToPublicRef = useRef(null);

  const handleMoveFileToPublic = (file) => {
    if (!file?.id || !propertyInfo?.id) return;
    // Show public confirmation dialog
    pendingMoveToPublicRef.current = file;
    setPublicConfirmText("");
    setPublicConfirmChecked(false);
    setShowPublicConfirmDialog(true);
  };

  const executeMoveFileToPublic = async (file) => {
    if (!file?.id || !propertyInfo?.id) return;
    try {
      await apiUpdateFile({
        id: file.id,
        authorId: effectiveAuthorId,
        isPublic: true,
        isSystem: false,
        ref: { id: propertyInfo.id, collection: fileRefCollection },
      });
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? { ...f, isPublic: true, isSystem: false, ref: { id: propertyInfo.id, collection: fileRefCollection } }
            : f
        )
      );
      myToast.success(`"${file.description || file.name || "File"}" moved to Public Files`);
    } catch (error) {
      console.error("Failed to move file to public:", error);
      myToast.error("Failed to move file");
    }
  };

  // dropZone: "system" | "public" | "property"
  const handleDrop = (dropZone = "property") => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRegularDragOver(false);
    setIsSystemDragOver(false);
    setIsPublicDragOver(false);

    // Without a selected contact/property, only the System zone accepts drops.
    if (!hasEntity && dropZone !== "system") return;

    // Check if this is an internal file being moved between sections
    const draggedFile = draggedFileRef.current;
    if (draggedFile?.id) {
      draggedFileRef.current = null;
      const draggedIsSystem = draggedFile.isSystem || !hasRef(draggedFile);
      const draggedIsPublic = Boolean(draggedFile.isPublic) && !draggedIsSystem;
      // Determine current zone of dragged file
      const fromZone = draggedIsSystem ? "system" : draggedIsPublic ? "public" : "property";
      if (fromZone !== dropZone) {
        if (dropZone === "system") handleMoveFileToSystem(draggedFile);
        else if (dropZone === "public") handleMoveFileToPublic(draggedFile);
        else handleMoveFileToProperty(draggedFile);
      }
      return;
    }

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      prepareDroppedFile(droppedFiles[0], dropZone === "system");
      if (dropZone === "public") setPendingIsPublic(true);
    }
  };

  const previewEntityIdProp =
    previewEntityType === "contact"
      ? { contactId: propertyInfo?.id }
      : { propertyId: propertyInfo?.id };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: { xs: 0, sm: "8px" },
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
        transition: "background-color 0.15s ease, outline 0.15s ease",
      })}
    >
      <Box
        sx={(theme) => ({
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.custom?.panelChrome || "#FFFFFF",
          ...addressBookPanelHeaderSx,
        })}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Tooltip title="Search files">
            <IconButton
              size="small"
              onClick={() => {
                if (searchOpen) setSearchQuery("");
                setSearchOpen((prev) => !prev);
              }}
              sx={{
                width: 24,
                height: 24,
                color: searchOpen ? "#334155" : "#64748B",
                border: "1px solid #E2E8F0",
                backgroundColor: searchOpen ? "#F1F5F9" : "#FFFFFF",
                "&:hover": {
                  color: "#334155",
                  backgroundColor: "#F8FAFC",
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add metadata entry (no file)">
            <IconButton
              size="small"
              onClick={() => setShowMetaOnlyDialog(true)}
              sx={{
                width: 24,
                height: 24,
                color: "#64748B",
                border: "1px solid #E2E8F0",
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  color: "#334155",
                  backgroundColor: "#F8FAFC",
                },
              }}
            >
              <NoteAddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontSize: "11px",
            }}
          >
            {filteredFiles.length + filteredDomainFiles.length} file
            {filteredFiles.length + filteredDomainFiles.length !== 1 ? "s" : ""}
          </Typography>
          {entityName ? (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 260,
                ml: 0.5,
              }}
              title={entityName}
            >
              {entityName}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onClose ? (
            <Tooltip title="Close">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onClose?.()}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0,
                    padding: 0,
                    color: "#64748B",
                    backgroundColor: "transparent",
                    "&:hover": {
                      color: "#334155",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      {searchOpen ? (
        <Box
          sx={(theme) => ({
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search files in this domain…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ sx: { fontSize: 13 } }}
          />
        </Box>
      ) : null}

      <input
        id="files-panel-input"
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json,.xml,.md,.mp4,.mov,.avi,.mp3,.wav,.zip"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      <Dialog
        open={showMetadataDialog}
        onClose={handleCloseMetadataDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: 0,
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "calc(100% - 24px)", sm: 560 },
            maxWidth: "560px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Add File</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <DialogContentText sx={{ mb: 1.25, fontSize: "0.85rem" }}>
            Choose category/type and optionally enter a description before adding this file.
          </DialogContentText>

          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "13px" }}>Category</InputLabel>
              <Select
                value={selectedCategory || ""}
                onChange={(e) => handleCategoryChange(e.target.value || "")}
                label="Category"
                sx={{ fontSize: "13px" }}
              >
                <MenuItem value="" sx={{ fontSize: "13px" }}>
                  <em>Optional</em>
                </MenuItem>
                {classSelectMenuItems}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "13px" }}>Type</InputLabel>
              <Select
                value={filesSelectedType || "Other"}
                onChange={(e) => setFilesSelectedType(e.target.value || "Other")}
                label="Type"
                sx={{ fontSize: "13px" }}
              >
                <MenuItem value="Other" sx={{ fontSize: "13px" }}>
                  Other
                </MenuItem>
                {typeSelectMenuItems}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Description (optional)"
            value={filesDisplayName}
            onChange={(e) => setFilesDisplayName(e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            placeholder="Tags, comma-separated (optional)"
            value={filesTags}
            onChange={(e) => setFilesTags(e.target.value)}
          />

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                checked={pendingIsSystem}
                onChange={(e) => setPendingIsSystem(e.target.checked)}
              />
            }
            label="System file"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={pendingIsPublic}
                onChange={(e) => setPendingIsPublic(e.target.checked)}
              />
            }
            label="Public file"
          />

          {pendingFile ? (
            <Typography sx={{ mt: 1, fontSize: "0.78rem", color: "#64748b" }}>
              {pendingFile.name}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMetadataDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmMetadataDialog}>
            Add File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metadata-only entry dialog: a record with no uploaded file */}
      <Dialog
        open={showMetaOnlyDialog}
        onClose={() => setShowMetaOnlyDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>Add Metadata</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Name"
            value={metaOnlyName}
            onChange={(e) => setMetaOnlyName(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "13px" }}>Category</InputLabel>
              <Select
                value={metaOnlyCategory || ""}
                onChange={(e) => setMetaOnlyCategory(e.target.value || "")}
                label="Category"
                sx={{ fontSize: "13px" }}
              >
                <MenuItem value="" sx={{ fontSize: "13px" }}>
                  <em>Optional</em>
                </MenuItem>
                {classSelectMenuItems}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "13px" }}>Type</InputLabel>
              <Select
                value={metaOnlyType || "Other"}
                onChange={(e) => setMetaOnlyType(e.target.value || "Other")}
                label="Type"
                sx={{ fontSize: "13px" }}
              >
                <MenuItem value="Other" sx={{ fontSize: "13px" }}>
                  Other
                </MenuItem>
                {typeSelectMenuItems}
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            placeholder="Notes"
            value={metaOnlyNotes}
            onChange={(e) => setMetaOnlyNotes(e.target.value)}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            placeholder="Tags, comma-separated (optional)"
            value={metaOnlyTags}
            onChange={(e) => setMetaOnlyTags(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMetaOnlyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateMetadataEntry}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Public File Confirmation Dialog */}
      <Dialog
        open={showPublicConfirmDialog}
        onClose={handleCancelPublicUpload}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#DC2626", fontWeight: 700 }}>
          Public File Warning
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: "0.9rem", color: "#334155" }}>
            You are about to upload a <strong>public file</strong>. This file will be accessible to anyone who has access to the link.
          </DialogContentText>
          <Typography variant="body2" sx={{ mb: 1.5, color: "#64748B", fontSize: "0.85rem" }}>
            To confirm, type <strong>public</strong> in the field below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type 'public' to confirm"
            value={publicConfirmText}
            onChange={(e) => setPublicConfirmText(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={publicConfirmChecked}
                onChange={(e) => setPublicConfirmChecked(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#475569" }}>
                I understand this file is visible to anyone who has access to the link
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPublicUpload}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={publicConfirmText.trim().toLowerCase() !== "public" || !publicConfirmChecked}
            onClick={handleConfirmPublicUpload}
          >
            Upload Public File
          </Button>
        </DialogActions>
      </Dialog>

      {filesStatus ? (
        <Box sx={{ width: "100%", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px" }}>
              {filesStatus}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px" }}>
              {filesProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={filesProgress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#3b82f6",
                borderRadius: 2,
              },
            }}
          />
        </Box>
      ) : null}

      {/* Files List — scrollable container */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          pb: "2px",
          minHeight: 0,
        }}
      >
        {/* ═══════ SYSTEM FILES ZONE ═══════ */}
        <Box
          onDragOver={handleDragOver(setIsSystemDragOver)}
          onDragLeave={handleDragLeave(setIsSystemDragOver)}
          onDrop={handleDrop("system")}
          sx={{
            borderRadius: "6px",
            mb: 0.5,
            backgroundColor: isSystemDragOver ? "#F5F3FF" : "transparent",
            outline: isSystemDragOver ? "2px dashed #7C3AED" : "none",
            transition: "background-color 0.15s ease, outline 0.15s ease",
          }}
        >
          {/* System Files Header */}
          <Box
            onClick={() => setIsSystemSectionOpen((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 0.5,
              px: 0.5,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F5F3FF" },
              borderRadius: "6px 6px 0 0",
            }}
          >
            <Tooltip title="Shared across properties" placement="top" arrow>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                <Divider sx={{ borderColor: "#C4B5FD" }} />
                <Divider sx={{ borderColor: "#C4B5FD" }} />
              </Box>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: "#7C3AED",
                fontSize: "11px",
                fontWeight: 800,
                ml: 1,
                whiteSpace: "nowrap",
              }}
            >
              System Files ({filteredDomainFiles.length})
            </Typography>
            <IconButton size="small" sx={{ color: "#7C3AED", p: 0.25, ml: 0.25 }}>
              {isSystemSectionOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>

          {isSystemSectionOpen && (
            <>
              {/* Domain System Files */}
              {withoutSuperseded(filteredDomainFiles).map((file, index) => (
                <Box
                  key={file.id}
                  draggable
                  onDragStart={(e) => {
                    draggedFileRef.current = file;
                    e.dataTransfer.setData("application/json", JSON.stringify(file));
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75, py: 0.75,
                    backgroundColor: index % 2 === 0 ? "#e3eeff" : "#eef4fcff",
                    borderBottom: "1px solid #e2e8f0",
                    "&:hover": { backgroundColor: "#dbeafe" },
                    cursor: "grab", "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", color: "#64748b" }}>{getFileIcon(file)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: "12px", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(file?.aiFileId || file?.aiFileID) && (
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 0.5, verticalAlign: "middle" }}>
                          <CheckCircleIcon sx={{ fontSize: "14px", color: "#7C3AED" }} />
                        </Box>
                      )}
                      {getFileDisplayName(file)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px", color: "#94a3b8", display: "block" }}>{`${file.type || "File"}${getFileCaptionExtras(file)}`}</Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openFilePreview(file); }} sx={{ width: 24, height: 24, color: "#64748b", "&:hover": { backgroundColor: "#e0e7ff", color: "#3b82f6" } }}>
                    <PreviewIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openFilePreview(file, { initialTab: 1, autoGenerateSummary: true }); }} sx={{ width: 24, height: 24, color: "#7C3AED", "&:hover": { backgroundColor: "#F3E8FF", color: "#6D28D9" } }}>
                    <AutoAwesomeIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadFile(file); }} sx={{ width: 24, height: 24, color: "#64748b", "&:hover": { backgroundColor: "#e0e7ff", color: "#3b82f6" } }}>
                    <DownloadIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopySystemFile(file); }} title="Copy to property files" sx={{ width: 24, height: 24, color: "#64748b", "&:hover": { backgroundColor: "#dbeafe", color: "#3b82f6" } }}>
                    <ContentCopyIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }} sx={{ width: 24, height: 24, color: "#9ca3af", "&:hover": { backgroundColor: "#fee2e2", color: "#ef4444" } }}>
                    <DeleteIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                </Box>
              ))}

              {/* System drop target / click to add */}
              <Box
                onClick={() => {
                  nextPickIsSystemRef.current = true;
                  nextPickIsPublicRef.current = false;
                  document.getElementById("files-panel-input")?.click();
                }}
                sx={{
                  px: 1.5, py: 2, textAlign: "center",
                  cursor: "pointer",
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": { backgroundColor: "#F5F3FF" },
                  borderRadius: "0 0 6px 6px",
                  border: "1px dashed #C4B5FD",
                  borderTop: "none",
                  mx: 0.5,
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ color: isSystemDragOver ? "#7C3AED" : "#94A3B8", fontSize: "11px", fontWeight: 600 }}>
                  Drag and drop or click to add a system file
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* ═══════ PUBLIC FILES ZONE ═══════ */}
        <Box
          onDragOver={handleDragOver(setIsPublicDragOver)}
          onDragLeave={handleDragLeave(setIsPublicDragOver)}
          onDrop={handleDrop("public")}
          sx={{
            borderRadius: "6px",
            mb: 0.5,
            backgroundColor: isPublicDragOver ? "#F0FDF4" : "transparent",
            outline: isPublicDragOver ? "2px dashed #22C55E" : "none",
            transition: "background-color 0.15s ease, outline 0.15s ease",
          }}
        >
          {/* Public Files Header */}
          <Box
            onClick={() => setIsPublicSectionOpen((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 0.5,
              px: 0.5,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F0FDF4" },
              borderRadius: "6px 6px 0 0",
            }}
          >
            <Tooltip title="Publicly accessible" placement="top" arrow>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                <Divider sx={{ borderColor: "#86EFAC" }} />
                <Divider sx={{ borderColor: "#86EFAC" }} />
              </Box>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: "#16A34A",
                fontSize: "11px",
                fontWeight: 800,
                ml: 1,
                whiteSpace: "nowrap",
              }}
            >
              Public Files ({publicFiles.length})
            </Typography>
            <IconButton size="small" sx={{ color: "#16A34A", p: 0.25, ml: 0.25 }}>
              {isPublicSectionOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>

          {isPublicSectionOpen && (
            <>
              {publicFiles.map((file, index) => (
                <Box
                  key={file.id}
                  draggable
                  onDragStart={(e) => {
                    draggedFileRef.current = file;
                    e.dataTransfer.setData("application/json", JSON.stringify(file));
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  onClick={(e) => { e.stopPropagation(); setPreviewFile(file); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75, py: 0.75,
                    backgroundColor: index % 2 === 0 ? "#ecfdf5" : "#f0fdf4",
                    borderBottom: "1px solid #e2e8f0",
                    "&:hover": { backgroundColor: "#dcfce7" },
                    cursor: "grab", "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", color: "#64748b" }}>{getFileIcon(file)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: "12px", color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(file?.aiFileId || file?.aiFileID) && (
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", mr: 0.5, verticalAlign: "middle" }}>
                          <CheckCircleIcon sx={{ fontSize: "14px", color: "#16A34A" }} />
                        </Box>
                      )}
                      {getFileDisplayName(file)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "10px", color: "#94a3b8", display: "block" }}>{`${file.type || "File"}${getFileCaptionExtras(file)}`}</Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openFilePreview(file); }} sx={{ width: 24, height: 24, color: "#64748b", "&:hover": { backgroundColor: "#dcfce7", color: "#16A34A" } }}>
                    <PreviewIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openFilePreview(file, { initialTab: 1, autoGenerateSummary: true }); }} sx={{ width: 24, height: 24, color: "#16A34A", "&:hover": { backgroundColor: "#dcfce7", color: "#15803d" } }}>
                    <AutoAwesomeIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadFile(file); }} sx={{ width: 24, height: 24, color: "#64748b", "&:hover": { backgroundColor: "#dcfce7", color: "#16A34A" } }}>
                    <DownloadIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }} sx={{ width: 24, height: 24, color: "#9ca3af", "&:hover": { backgroundColor: "#fee2e2", color: "#ef4444" } }}>
                    <DeleteIcon sx={{ fontSize: "14px" }} />
                  </IconButton>
                </Box>
              ))}

              {/* Public drop target / click to add */}
              <Box
                onClick={() => {
                  if (!hasEntity) return;
                  nextPickIsSystemRef.current = false;
                  nextPickIsPublicRef.current = true;
                  document.getElementById("files-panel-input")?.click();
                }}
                sx={{
                  px: 1.5, py: 2, textAlign: "center",
                  cursor: hasEntity ? "pointer" : "not-allowed",
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: hasEntity ? 1 : 0.5,
                  "&:hover": hasEntity ? { backgroundColor: "#F0FDF4" } : undefined,
                  borderRadius: "0 0 6px 6px",
                  border: "1px dashed #86EFAC",
                  borderTop: "none",
                  mx: 0.5,
                  mb: 0.5,
                }}
                title={hasEntity ? undefined : emptySelectionMessage}
              >
                <Typography variant="caption" sx={{ color: isPublicDragOver ? "#16A34A" : "#94A3B8", fontSize: "11px", fontWeight: 600 }}>
                  {hasEntity
                    ? "Drag and drop or click to add a public file"
                    : emptySelectionMessage}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* ═══════ PROPERTY / CONTACT FILES ZONE ═══════ */}
        <Box
          onDragOver={handleDragOver(setIsRegularDragOver)}
          onDragLeave={handleDragLeave(setIsRegularDragOver)}
          onDrop={handleDrop("property")}
          sx={{
            borderRadius: "6px",
            backgroundColor: isRegularDragOver ? "#EFF6FF" : "transparent",
            outline: isRegularDragOver ? "2px dashed #3B82F6" : "none",
            transition: "background-color 0.15s ease, outline 0.15s ease",
          }}
        >
          {/* Property/Contact Files Header */}
          <Box
            onClick={() => setIsPropertySectionOpen((v) => !v)}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 0.5,
              px: 0.5,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#EFF6FF" },
              borderRadius: "6px 6px 0 0",
            }}
          >
            <Tooltip title={`Private to this ${fileSettingsType === "contact" ? "contact" : "property"}`} placement="top" arrow>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                <Divider sx={{ borderColor: "#93C5FD" }} />
                <Divider sx={{ borderColor: "#93C5FD" }} />
              </Box>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: "#1E40AF",
                fontSize: "11px",
                fontWeight: 800,
                ml: 1,
                whiteSpace: "nowrap",
              }}
            >
              {fileSettingsType === "contact" ? "Contact Files" : "Property Files"} ({entityFiles.length})
            </Typography>
            <IconButton size="small" sx={{ color: "#1E40AF", p: 0.25, ml: 0.25 }}>
              {isPropertySectionOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Box>

          {isPropertySectionOpen && (
            <>
              {Object.keys(groupedFiles).length > 0 && (
                Object.keys(groupedFiles).map((fileClass) => (
                  <React.Fragment key={fileClass}>
                    <Box sx={{ display: "flex", alignItems: "center", py: 0.25, mt: Object.keys(groupedFiles).indexOf(fileClass) > 0 ? 0.5 : 0 }}>
                      <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "10px", fontWeight: 500, mr: 1 }}>{fileClass}</Typography>
                      <Divider sx={{ flex: 1, borderColor: "#E2E8F0", borderBottomWidth: "0.5px" }} />
                    </Box>
                    {groupedFiles[fileClass].map((file, index) => (
                      <Box
                        key={file.id}
                        draggable
                        onDragStart={(e) => {
                          draggedFileRef.current = file;
                          e.dataTransfer.setData("application/json", JSON.stringify(file));
                          e.dataTransfer.effectAllowed = "copy";
                        }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1, py: 1,
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      borderBottom: "1px solid #e2e8f0",
                      "&:hover": { backgroundColor: "#f0f9ff" },
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#64748b",
                      }}
                    >
                      {getFileIcon(file)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          color: "#334155",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(file?.aiFileId || file?.aiFileID) && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              mr: 0.5,
                              verticalAlign: "middle",
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ fontSize: "14px", color: "#7C3AED" }}
                            />
                          </Box>
                        )}
                        {getFileDisplayName(file)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "10px",
                          color: "#94a3b8",
                          display: "block",
                        }}
                      >
                        {`${file.type || "File"} • ${
                          file.isPublic ? "Public" : "Private"
                        }${getFileCaptionExtras(file)}`}
                      </Typography>
                    </Box>

                    {/* Preview - available for all files */}
                    <IconButton
                      size="small"
                      onClick={() => openFilePreview(file)}
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#64748b",
                        "&:hover": {
                          backgroundColor: "#e0e7ff",
                          color: "#3b82f6",
                        },
                      }}
                    >
                      <PreviewIcon sx={{ fontSize: "14px" }} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() =>
                        openFilePreview(file, {
                          initialTab: 1,
                          autoGenerateSummary: true,
                        })
                      }
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#7C3AED",
                        "&:hover": {
                          backgroundColor: "#F3E8FF",
                          color: "#6D28D9",
                        },
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: "14px" }} />
                    </IconButton>

                    {/* Download - available for all files */}
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadFile(file)}
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#64748b",
                        "&:hover": {
                          backgroundColor: "#e0e7ff",
                          color: "#3b82f6",
                        },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: "14px" }} />
                    </IconButton>

                    {/* Conditional actions based on file type */}
                    {isSystemFile(file) ? (
                      /* System files: Copy button only */
                      <IconButton
                        size="small"
                        onClick={() => handleCopySystemFile(file)}
                        title="Copy to property files"
                        sx={{
                          width: 24,
                          height: 24,
                          color: "#64748b",
                          "&:hover": {
                            backgroundColor: "#dbeafe",
                            color: "#3b82f6",
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: "14px" }} />
                      </IconButton>
                    ) : (
                      /* Regular files: Delete button */
                      <>
                        <FileVisibilityToggle
                          isPublic={Boolean(file?.isPublic)}
                          onToggle={() => handleToggleFileVisibility(file)}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete ${getFileDisplayName(file)}?`
                              )
                            ) {
                              handleDeleteFile(file);
                            }
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            color: "#64748b",
                            "&:hover": {
                              backgroundColor: "#fee2e2",
                              color: "#ef4444",
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                ))}
              </React.Fragment>
                ))
              )}

              {/* Property drop target */}
              <Box
                onClick={() => {
                  if (!hasEntity) return;
                  nextPickIsSystemRef.current = false;
                  nextPickIsPublicRef.current = false;
                  document.getElementById("files-panel-input")?.click();
                }}
                sx={{
                  px: 1.5, py: 2, textAlign: "center",
                  cursor: hasEntity ? "pointer" : "not-allowed",
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: hasEntity ? 1 : 0.5,
                  "&:hover": hasEntity ? { backgroundColor: "#EFF6FF" } : undefined,
                  borderRadius: "0 0 6px 6px",
                  border: "1px dashed #93C5FD",
                  borderTop: "none",
                  mx: 0.5,
                  mb: 0.5,
                }}
                title={hasEntity ? undefined : emptySelectionMessage}
              >
                <Typography variant="caption" sx={{ color: isRegularDragOver ? "#1D4ED8" : "#94A3B8", fontSize: "11px", fontWeight: 600 }}>
                  {hasEntity
                    ? "Drag and drop or click to add a file"
                    : emptySelectionMessage}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          height: 28,
          minHeight: 28,
          maxHeight: 28,
          px: 1.5,
          py: 0.25,
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.custom?.panelChrome || "#FFFFFF",
        })}
      >
        <FolderIcon sx={{ fontSize: 14, color: "#111827", display: "block" }} />
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#111827", lineHeight: 1 }}>
          Files
        </Typography>
        {entityName ? (
          <Typography sx={{ fontSize: "0.7rem", color: "#111827", lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
            — {entityName}
          </Typography>
        ) : null}
      </Box>

      <PreviewFile
        open={!!previewFile}
        file={previewFile}
        propertyName={propertyInfo?.name || ""}
        identity={propertyInfo?.identity || {}}
        {...previewEntityIdProp}
        agents={agents}
        loadingAgents={loadingAgents}
        initialTab={previewInitialTab}
        autoGenerateSummary={previewAutoGenerateSummary}
        onClose={() => {
          setPreviewFile(null);
          setPreviewInitialTab(0);
          setPreviewAutoGenerateSummary(false);
        }}
        onDownload={handleDownloadFile}
        onSaveSummary={handleSaveSummary}
        isDomainFile={previewFile?.isDomainFile || false}
      />
    </Box>
  );
};

export default FilesPanel;
